// Плагин ProseMirror «Разметка страницы»: после каждой правки (в том же кадре, до отрисовки) измеряет поток,
// разбивает его на страницы (engine.ts) и ставит распорки-виджеты: между блоками, внутри абзаца (строка уходит на
// следующий лист), между строками таблицы (с «Продолжение таблицы N» и копией головки), между строками кода листинга
// (с «Продолжение листинга N» и рамками частей - code.ts). Документ и экспорт не меняются - только отображение.
// Листы, номера страниц и сноски рисует слой PagesLayer (React).
import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { measure, paraHeights, type Measured } from './measure';
import { paginate, pageOfAtom } from './engine';
import { contentTop, LINE_14_SINGLE, PT, STRIDE, totalHeight, BODY_TOP, BODY_H } from './geometry';
import type { Atom, Page, Spacer } from './types';
import { pagesStore, type Mask, type PageView } from './store';
import { syncAutoWidths } from './tablewidths';
import { applyCodeBreaks, codeViews, type CodeBreak } from './code';
import { NBSP } from '../../core/schema/constants';

interface PState { deco: DecorationSet; spacers: Spacer[]; dirty: number }

export const pagesKey = new PluginKey<PState>('stoPages');

/** «Продолжение таблицы N» / «Продолжение листинга N»: строка TNR 14 одинарная + 6 пт после (стиль названия таблицы и
 *  подписи листинга - одинаковые интервалы; интервал перед в начале страницы не учитывается) */
export const CONT_LABEL_H = LINE_14_SINGLE + 6 * PT;

// ------------------------------------------------------------------------------------------------ поля блоков
const px = (v: string) => parseFloat(v) || 0;
function effMarginTop(el: Element | null): number {
  let m = 0;
  let e: Element | null = el;
  for (let g = 0; e && g < 6; g++) {
    const cs = getComputedStyle(e);
    m = Math.max(m, px(cs.marginTop));
    if (px(cs.paddingTop) || px(cs.borderTopWidth) || cs.display === 'flex' || cs.display === 'grid' || cs.display.includes('table')) break;
    e = e.firstElementChild;
  }
  return m;
}
function effMarginBottom(el: Element | null): number {
  let m = 0;
  let e: Element | null = el;
  for (let g = 0; e && g < 6; g++) {
    const cs = getComputedStyle(e);
    m = Math.max(m, px(cs.marginBottom));
    if (px(cs.paddingBottom) || px(cs.borderBottomWidth) || cs.display === 'flex' || cs.display === 'grid' || cs.display.includes('table')) break;
    e = e.lastElementChild;
  }
  return m;
}
function prevElement(el: Element | null): Element | null {
  let p = el ? el.previousElementSibling : null;
  while (p && p.classList.contains('pg-sp')) p = p.previousElementSibling;
  return p;
}

// ------------------------------------------------------------------------------------------------ распорки
export function buildSpacers(atoms: Atom[], pages: Page[]): Spacer[] {
  const out: Spacer[] = [];
  for (let q = 1; q < pages.length; q++) {
    const pg = pages[q];
    const pp = pages[q - 1];
    const a = atoms[pg.start];
    const ap = atoms[pg.start - 1];
    if (!a || !a.brk || (a.pos < 0 && a.brk !== 'code' && a.brk !== 'dom')) continue;
    const dS = pg.shift - pp.shift;
    const g = ap ? a.top - ap.bottom : 0;
    let h = dS + g;
    if (a.brk === 'block') {
      let mb: number;
      let mt: number;
      if (a.mbMeas !== undefined && a.mtMeas !== undefined) { mb = a.mbMeas; mt = a.mtMeas; } else {
        const k = 1;
        mb = effMarginBottom(prevElement(a.outer || null)) / k;
        mt = effMarginTop(a.outer || null) / k;
      }
      h -= Math.max(0, mb) + Math.max(0, mt);
    }
    h -= a.spC || 0;
    // ошибка оценки высоты листинга CodeMirror между началом прошлой страницы и этой - на экране она уже есть
    h -= (a.errCum || 0) - (atoms[pp.start]?.errCum || 0);
    const sp: Spacer = { kind: a.brk, pos: a.pos, side: a.brk === 'inline' || a.brk === 'row' ? -1 : (a.side ?? 1), h: Math.max(0, h), page: q };
    if (a.brk === 'code') {
      sp.codeId = a.codeId; sp.codeLine = a.codeLine; sp.pos = -1;
      if (pg.lcont) {
        // «Продолжение/Окончание листинга N» над следующей частью (как writer: номер - как в подписи, пробел - как в
        // тексте, неразрывный только при USE_NBSP)
        sp.overhead = pg.overhead;
        sp.listing = pg.lcont.listing;
        sp.label = (pg.lcont.final ? 'Окончание' : 'Продолжение') + ' листинга' + NBSP + (pg.lcont.listing.num || '');
      }
    }
    if (a.brk === 'dom') { sp.el = a.el; sp.pos = -1; }
    if (pg.cont) {
      sp.overhead = pg.overhead;
      sp.table = pg.cont.table;
      // «Продолжение таблицы N» (writer/tablesplit.py: пробел - как в тексте, неразрывный только при USE_NBSP); без
      // подписи - только повтор головки (таблицу, у которой на первой странице одна головка, writer не делит)
      sp.label = pg.cont.label ? (pg.cont.final ? 'Окончание' : 'Продолжение') + ' таблицы' + NBSP + (pg.cont.table.num || '') : '';
    }
    out.push(sp);
  }
  return out;
}

/** Распорки вне ProseMirror (позиции списка источников): отступ сверху у элемента. */
let domBroken = new Set<HTMLElement>();
function applyDomBreaks(list: Spacer[]) {
  const next = new Set<HTMLElement>();
  for (const sp of list) {
    const el = sp.el;
    if (!el) continue;
    const h = Math.round(sp.h * 64) / 64;
    next.add(el);
    if (el.getAttribute('data-pg-h') !== String(h)) {
      el.style.marginTop = h + 'px';
      el.setAttribute('data-pg-h', String(h));
      el.classList.add('pg-sp-dom');
    }
  }
  for (const el of domBroken) {
    if (!next.has(el)) { el.style.marginTop = ''; el.removeAttribute('data-pg-h'); el.classList.remove('pg-sp-dom'); }
  }
  domBroken = next;
}

function rowSpacerDom(sp: Spacer): HTMLElement {
  const tr = document.createElement('tr');
  tr.className = 'pg-sp pg-sp-row';
  tr.contentEditable = 'false';
  tr.setAttribute('data-h', String(Math.round(sp.h * 64) / 64));
  const td = document.createElement('td');
  td.className = 'pg-sp-cell';
  td.colSpan = Math.max(1, sp.table?.cols || 1);
  const fill = document.createElement('div');
  fill.className = 'pg-sp-fill';
  fill.style.height = Math.round(Math.max(0, sp.h - (sp.overhead || 0)) * 64) / 64 + 'px';
  td.appendChild(fill);
  if (sp.overhead) {
    if (sp.label) {
      const lab = document.createElement('div');
      lab.className = 'pg-cont-label';
      lab.style.height = CONT_LABEL_H + 'px';
      lab.textContent = sp.label;
      td.appendChild(lab);
    }
    const src = sp.table?.table;
    if (src && sp.table!.headRows) {
      const t = document.createElement('table');
      t.className = 'pg-head-clone';
      const cg = document.createElement('colgroup');
      const firstRow = src.rows[0];
      const total = src.getBoundingClientRect().width || 1;
      // ширины граф - как у таблицы на экране (в долях), головка - копия строк
      const widths: number[] = [];
      if (firstRow) for (const c of Array.from(firstRow.cells)) {
        const w = c.getBoundingClientRect().width / total;
        const span = c.colSpan || 1;
        for (let s = 0; s < span; s++) widths.push(w / span);
      }
      for (const w of widths) { const col = document.createElement('col'); col.style.width = (w * 100).toFixed(3) + '%'; cg.appendChild(col); }
      t.appendChild(cg);
      const tb = document.createElement('tbody');
      for (let r = 0; r < sp.table!.headRows && r < src.rows.length; r++) {
        const row = src.rows[r].cloneNode(true) as HTMLElement;
        row.removeAttribute('class');
        row.querySelectorAll('.column-resize-handle, .selectedCell').forEach((x) => x.classList.remove('selectedCell'));
        tb.appendChild(row);
      }
      t.appendChild(tb);
      td.appendChild(t);
    }
  }
  tr.appendChild(td);
  return tr;
}

export function spacerDeco(sp: Spacer): Decoration {
  const hh = Math.round(sp.h * 64) / 64;       // единица вёрстки Chrome - 1/64 px: без накопления ошибки
  const key = `pg:${sp.kind}:${hh}:${sp.overhead || 0}:${sp.label || ''}`;
  const toDOM = () => {
    if (sp.kind === 'row') return rowSpacerDom(sp);
    const el = document.createElement(sp.kind === 'inline' ? 'span' : 'div');
    el.className = 'pg-sp ' + (sp.kind === 'inline' ? 'pg-sp-inline' : 'pg-sp-block');
    el.contentEditable = 'false';
    el.style.height = hh + 'px';
    el.setAttribute('data-h', String(hh));
    el.setAttribute('aria-hidden', 'true');
    return el;
  };
  return Decoration.widget(sp.pos, toDOM, { side: sp.side, key, ignoreSelection: true, marks: [] });
}

/** Число распорок-виджетов в наборе декораций (без декораций строк таблиц «рамка открыта снизу»). */
function spacerWidgets(deco: DecorationSet): number {
  return deco.find(undefined, undefined, (spec) => typeof spec.key === 'string' && spec.key.startsWith('pg:')).length;
}

/** Все распорки списка стоят в наборе декораций: виджет на своей позиции. Набор и список отображаются по правкам
 *  отдельно, и виджет на границе заменённого узла (setNodeMarkup: правка кода листинга, атрибутов рисунка, таблицы)
 *  из набора пропадает, а в списке остаётся - тогда пересчёт видел «те же» распорки и не возвращал пропавшую:
 *  листинг и всё ниже него наезжали на промежуток между листами до следующей правки, меняющей разметку. */
function spacersInDeco(deco: DecorationSet, spacers: Spacer[]): boolean {
  const want = spacers.filter((sp) => sp.kind !== 'code' && sp.kind !== 'dom');
  if (spacerWidgets(deco) !== want.length) return false;
  return want.every((sp) => deco.find(sp.pos, sp.pos, (spec) => typeof spec.key === 'string' && spec.key.startsWith('pg:')).length > 0);
}

export function sameSpacers(a: Spacer[], b: Spacer[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.kind !== y.kind || x.pos !== y.pos || x.side !== y.side || (x.label || '') !== (y.label || '') || Math.abs((x.overhead || 0) - (y.overhead || 0)) > 0.5) return false;
    if (Math.abs(x.h - y.h) > 0.03) return false;
  }
  return true;
}

// ------------------------------------------------------------------------------------------------ пересчёт
export interface LayoutResult { measured: Measured; pages: Page[] }
let lastLayout: LayoutResult | null = null;
let resizeChain = 0;
let sigHist: string[] = [];
export const getLastLayout = () => lastLayout;
/** Отладка и автотесты: window.stoPages.layout() - последняя разметка, check() - расхождения с листами. */
(window as any).stoPages = {
  layout: () => lastLayout,
  /** движок страниц на готовых атомах (автотесты правил «не отрывать», продолжения таблиц) */
  paginate: (atoms: Atom[], opt?: Partial<Parameters<typeof paginate>[1]>) =>
    paginate(atoms, { fnHeight: () => 0, fnSep: 0, contLabelH: CONT_LABEL_H, ...(opt || {}) }),
  relayout: () => { const ed = (window as any).sto?.editor; if (ed) relayout(ed.view); },
  state: () => { const s = pagesStore.getState(); return { ready: s.ready, front: s.frontCount, main: s.main.length, total: s.frontCount + s.main.length, current: s.current, heads: s.heads, ms: s.ms, runs: s.runs, fronts: s.front }; },
  /** Свежий замер: где на самом деле стоят первая и последняя строки каждой страницы (px без масштаба). Низ последней
   *  строки - без ошибки оценки высоты строк листинга CodeMirror на этой странице (errCum: CodeMirror ещё не отрисовал
   *  строки вне окна; когда отрисует - рамка листинга станет расчётной высоты, разметка пересчитается). */
  check: () => {
    const ed = (window as any).sto?.editor;
    if (!ed) return null;
    const view: EditorView = ed.view;
    const host = hostOf(view);
    if (!host) return null;
    const m = measure(view, host);
    if (!m) return null;
    const pages = paginate(m.atoms, { fnHeight, fnSep: fnSepHeight(), contLabelH: CONT_LABEL_H });
    return pages.map((p, i) => {
      const a = m.atoms[p.start];
      const b = m.atoms[p.end];
      return { page: i + 1, kind: a?.kind, want: +(contentTop(i) + p.overhead).toFixed(1), got: a ? +a.dTop.toFixed(1) : null,
        lastGot: b ? +(b.dBottom - ((b.errCum || 0) - (a?.errCum || 0))).toFixed(1) : null, limit: +(contentTop(i) + BODY_H - p.fnH).toFixed(1),
        text: (a?.el?.textContent || '').replace(/\s+/g, ' ').slice(0, 50) };
    });
  },
  /** Тексты первых и последних n строк каждой страницы (для сравнения с PDF из Word). */
  pageLines: (n = 3) => {
    const ed = (window as any).sto?.editor;
    if (!ed) return null;
    const view: EditorView = ed.view;
    const host = hostOf(view);
    if (!host) return null;
    const m = measure(view, host);
    if (!m) return null;
    const atoms = m.atoms;
    const pages = paginate(atoms, { fnHeight, fnSep: fnSepHeight(), contLabelH: CONT_LABEL_H });
    const doc = view.state.doc;
    const textOf = (a: Atom, next: Atom | undefined): string => {
      const el = a.el;
      if (!el) return '';
      if (a.codeId && a.codeLine) {
        const cm = codeViews.get(a.codeId);
        try { return cm ? cm.state.doc.line(a.codeLine).text : ''; } catch { return ''; }
      }
      if (a.kind === 'line' || a.kind === 'para') {
        const pos0 = a.li === 0 ? posInside(view, el) : a.pos;
        let end = posEnd(view, el);
        if (next && next.el === el && next.kind === 'line') { const q = next.pos; if (q > 0) end = q; }
        if (pos0 < 0 || end <= pos0) return (el.textContent || '');
        return doc.textBetween(pos0, end, ' ', ' ');
      }
      const num = el.getAttribute?.('data-num');
      if (num !== null && num !== undefined) return num + (el.textContent || '');
      const lab = el.getAttribute?.('data-label');
      if (lab && !el.classList.contains('sto-caption')) return lab + ' ' + (el.getAttribute('data-status') ? '(' + el.getAttribute('data-status') + ') ' : '') + (el.textContent || '');
      const cap = (el.classList?.contains('sto-caption') ? el : el.querySelector?.('.sto-caption')) as HTMLElement | null;
      if (cap) return (cap.getAttribute('data-label') || '') + (cap.textContent || '');
      return el.textContent || '';
    };
    return {
      front: pagesStore.getState().frontCount,
      pages: pages.map((p) => {
        // абзацы страницы - в строки (первые и последние n)
        const first: string[] = [];
        const last: string[] = [];
        if (p.cont?.label) first.push((p.cont.final ? 'Окончание' : 'Продолжение') + ' таблицы ' + p.cont.table.num);
        if (p.lcont) first.push((p.lcont.final ? 'Окончание' : 'Продолжение') + ' листинга ' + p.lcont.listing.num);
        for (let j = p.start; j <= p.end && first.length < n; j++) {
          if (atoms[j].kind === 'para' && atoms[j].expand) { const c = atoms[j].expand!(); atoms[j].expand = undefined; atoms.splice(j, 1, ...c); p.end += c.length - 1; for (const q of pages) if (q.start > j) { q.start += c.length - 1; q.end += c.length - 1; } }
          const t = textOf(atoms[j], atoms[j + 1]).replace(/\s+/g, ' ').trim();
          if (t) first.push(t);
        }
        for (let j = p.end; j >= p.start && last.length < n; j--) {
          const t = textOf(atoms[j], atoms[j + 1]).replace(/\s+/g, ' ').trim();
          if (t) last.unshift(t);
        }
        return { first, last };
      }),
    };
  },
};
function posInside(view: EditorView, el: HTMLElement): number {
  try { return view.posAtDOM(el, 0); } catch { return -1; }
}
function posEnd(view: EditorView, el: HTMLElement): number {
  try { return view.posAtDOM(el, el.childNodes.length); } catch { return -1; }
}

function hostOf(view: EditorView): HTMLElement | null {
  return (view.dom as HTMLElement).closest('.sheet-main') as HTMLElement | null;
}

export function pagesEnabled(view: EditorView): boolean {
  const h = hostOf(view);
  return !!h && h.classList.contains('pages-mode');
}

/** Высоты сносок: элементы-измерители слоя листов (data-fn-measure) или оценка - одна строка 10 пт. */
function fnHeight(id: string): number {
  const el = document.querySelector(`[data-fn-measure="${CSS.escape(id)}"]`) as HTMLElement | null;
  if (el) return el.offsetHeight;
  return 11.5 * PT;
}

export function layoutNow(view: EditorView, why = ''): void {
  if (view.isDestroyed) return;
  const host = hostOf(view);
  const st = pagesKey.getState(view.state);
  if (!host || !st) return;
  if (!host.classList.contains('pages-mode')) {
    if (st.spacers.length) view.dispatch(view.state.tr.setMeta(pagesKey, { deco: DecorationSet.empty, spacers: [], dirty: st.dirty }).setMeta('addToHistory', false));
    host.style.minHeight = '';
    applyCodeBreaks(new Map());
    applyDomBreaks([]);
    if (pagesStore.getState().ready) pagesStore.getState().set({ ready: false, main: [], masks: [] });
    lastLayout = null;
    return;
  }
  const t0 = performance.now();
  syncAutoWidths(view);
  const measured = measure(view, host);
  if (!measured) return;
  const t1 = performance.now();
  const atoms = measured.atoms;
  const pages = paginate(atoms, { fnHeight, fnSep: fnSepHeight(), contLabelH: CONT_LABEL_H });
  const t2 = performance.now();
  const spacers = buildSpacers(atoms, pages);
  const openRows = pages.map((p) => (p.openRow !== undefined ? atoms[p.openRow].npos ?? -1 : -1)).filter((x) => x >= 0);
  lastLayout = { measured, pages };
  host.style.minHeight = totalHeight(pages.length) + 'px';
  let changed = !sameSpacers(spacers, st.spacers) || !spacersInDeco(st.deco, st.spacers)
    || openRowsSig(openRows) !== openRowsSig(curOpenRows(st));
  // предохранитель от «качелей»: разметка, которая меняется от своей же перестановки (замер после распорок даёт
  // другое решение), не должна пересчитываться бесконечно - после 10 изменений подряд без правки текста стоп
  // Признак «качелей» - разметка ВОЗВРАЩАЕТСЯ к уже бывшему варианту (A→B→A→B) при пересчётах от изменения размера
  // (следствие своих же распорок). Загрузка картинок, шрифтов, правка текста дают новые варианты - это не качели.
  if (changed) {
    const sig = spacers.map((x) => x.kind + x.pos + ':' + Math.round(x.h)).join('|');
    // счётчик «качелей» - только для пересчётов подряд без правки (раньше он копился за всю работу с документом, и
    // после нескольких возвратов к прежнему варианту разметка переставала обновляться при изменении размеров)
    if (why !== 'resize') { sigHist = []; resizeChain = 0; }
    else if (sigHist.includes(sig) && ++resizeChain > 3) { changed = false; (window as any).__stoPagesDebug && ((window as any).__stoPagesDebug.frozen = true); }
    sigHist.push(sig);
    if (sigHist.length > 6) sigHist.shift();
  } else if (why !== 'resize') { sigHist = []; resizeChain = 0; }
  // распорки в листингах (CodeMirror) - отдельно от декораций ProseMirror
  const code = new Map<string, CodeBreak[]>();
  for (const sp of spacers) if (sp.kind === 'code' && sp.codeId) {
    const l = code.get(sp.codeId) || [];
    const L = sp.listing;
    l.push({ line: sp.codeLine || 1, h: Math.round(sp.h * 64) / 64, ...(L && sp.label ? {
      label: sp.label, labelH: CONT_LABEL_H, padTop: L.padTop, textDx: L.textDx, frameDx: L.frameDx, frameW: L.frameW,
    } : {}) });
    code.set(sp.codeId, l);
  }
  applyCodeBreaks(code);
  applyDomBreaks(spacers.filter((sp) => sp.kind === 'dom'));
  if (changed) {
    const decos = spacers.filter((sp) => sp.kind !== 'code' && sp.kind !== 'dom').map(spacerDeco);
    for (const p of openRows) {
      const n = view.state.doc.nodeAt(p);
      if (n) decos.push(Decoration.node(p, p + n.nodeSize, { class: 'pg-row-open' }, { pgOpen: true }));
    }
    view.dispatch(view.state.tr.setMeta(pagesKey, { deco: DecorationSet.create(view.state.doc, decos), spacers, dirty: st.dirty }).setMeta('addToHistory', false));
  }
  publish(view, host, atoms, pages, performance.now() - t0, masksFor(atoms, pages, measured));
  const dbg = (window as any).__stoPagesDebug || { runs: 0, changes: 0, log: [] };
  dbg.runs++; if (changed) dbg.changes++;
  dbg.why = why; dbg.changed = changed; dbg.ms = performance.now() - t0; dbg.pages = pages.length;
  dbg.t = { measure: +(t1 - t0).toFixed(2), paginate: +(t2 - t1).toFixed(2), rest: +(performance.now() - t2).toFixed(2) };
  if (changed) {
    const diff: string[] = [];
    const n = Math.max(spacers.length, st.spacers.length);
    for (let i = 0; i < n && diff.length < 3; i++) {
      const x = spacers[i]; const y = st.spacers[i];
      if (!x || !y || x.pos !== y.pos || Math.abs(x.h - y.h) > 0.03) diff.push(`${i}: ${y ? y.kind + '@' + y.pos + ' h' + y.h.toFixed(1) : '-'} -> ${x ? x.kind + '@' + x.pos + ' h' + x.h.toFixed(1) : '-'}`);
    }
    dbg.log.push(why + ' ' + diff.join(' | '));
    if (dbg.log.length > 40) dbg.log.shift();
  }
  (window as any).__stoPagesDebug = dbg;
}

function openRowsSig(a: number[]) { return a.join(','); }
function curOpenRows(st: PState): number[] {
  return st.deco.find(undefined, undefined, (spec) => !!spec.pgOpen).map((d) => d.from);
}

let fnSepCache = 0;
function fnSepHeight() {
  if (!fnSepCache) {
    const el = document.querySelector('.pg-fn-sep-measure') as HTMLElement | null;
    fnSepCache = el?.offsetHeight || 0;
  }
  return fnSepCache || 16.1 * PT;
}

function snippet(a: Atom | undefined): string {
  const el = a?.el;
  if (!el) return '';
  return (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 90);
}

/** Листинг, разорванный между страницами: полосы, закрывающие боковые линии рамки ниже последней строки части на
 *  странице (нижней линии у неё нет - рамка открыта снизу, как в DOCX) и выше надписи «Продолжение/Окончание листинга
 *  N» на следующей (в DOCX - отдельные части в своих рамках; рядом с надписью линии закрывает её белая полоса - code.ts). */
function masksFor(atoms: Atom[], pages: Page[], m: Measured): Mask[] {
  const out: Mask[] = [];
  for (let q = 1; q < pages.length; q++) {
    const a = atoms[pages[q].start];
    const b = atoms[pages[q].start - 1];
    if (!a || a.brk !== 'code' || !a.el || !b) continue;
    const r = a.el.getBoundingClientRect();
    const left = (r.left - m.left) / m.k - 3;
    const width = r.width / m.k + 6;
    out.push({ top: b.bottom + pages[q - 1].shift, bottom: a.top + pages[q].shift - (pages[q].lcont ? pages[q].overhead : 0), left, width });
  }
  return out;
}

function publish(view: EditorView, host: HTMLElement, atoms: Atom[], pages: Page[], ms: number, masks: Mask[] = []) {
  const main: PageView[] = pages.map((p, i) => ({
    n: i + 1,
    lastBottom: p.lastBottom - i * STRIDE,
    fns: p.fns,
    fnH: p.fnH,
    cont: p.cont ? { num: p.cont.table.num, final: p.cont.final } : undefined,
    snippet: snippet(atoms[p.start]),
    pos: atoms[p.start] && atoms[p.start].pos >= 0 ? atoms[p.start].pos : 0,
  }));
  const heads: Record<string, number> = {};
  atoms.forEach((a, idx) => { if (a.nodeId && heads[a.nodeId] === undefined) heads[a.nodeId] = pageOfAtom(pages, idx) + 1; });
  const s = pagesStore.getState();
  const same = s.ready && s.main.length === main.length && s.main.every((m, i) => m.fns.join() === main[i].fns.join()
    && Math.abs(m.lastBottom - main[i].lastBottom) < 1 && (m.cont?.num || '') === (main[i].cont?.num || '') && m.snippet === main[i].snippet)
    && JSON.stringify(s.heads) === JSON.stringify(heads);
  const patch: any = { at: Date.now(), ms, runs: s.runs + 1 };
  if (!same) Object.assign(patch, { ready: true, main, heads });
  if (JSON.stringify(masks) !== JSON.stringify(s.masks)) patch.masks = masks;
  s.set(patch);
  updateCurrentPage(view);
}

/** Страница курсора: по координате курсора на листе. */
export function updateCurrentPage(view: EditorView) {
  const host = hostOf(view);
  if (!host || view.isDestroyed) return;
  // курсор в малом редакторе листа перед текстом (задание, реферат) - страницу показывает он (front.ts)
  if (!view.hasFocus() && (document.activeElement as HTMLElement | null)?.closest?.('.pg-front .ProseMirror')) return;
  const s = pagesStore.getState();
  if (!s.ready) return;
  let idx = 0;
  try {
    const c = view.coordsAtPos(view.state.selection.head);
    const r = host.getBoundingClientRect();
    const k = r.width / host.offsetWidth || 1;
    idx = Math.max(0, Math.min(s.main.length - 1, Math.floor(((c.top + c.bottom) / 2 - r.top) / k / STRIDE)));
  } catch { return; }
  const cur = s.frontCount + idx + 1;
  const total = s.frontCount + s.main.length;
  if (cur !== s.current || total !== s.total) s.set({ current: cur, total });
}

// ------------------------------------------------------------------------------------------------ щелчки и стрелки
function sheetY(view: EditorView, clientY: number): { y: number; k: number; host: HTMLElement } | null {
  const host = hostOf(view);
  if (!host) return null;
  const r = host.getBoundingClientRect();
  const k = r.width / 793.7007874;
  return { y: (clientY - r.top) / k, k, host };
}

/** Щелчок в пустой низ листа / промежуток между листами -> конец последней строки листа; в верхнее поле - первая строка. */
function deadZoneClick(view: EditorView, e: MouseEvent): boolean {
  if (e.button !== 0 || !pagesEnabled(view)) return false;
  const t = e.target as HTMLElement;
  if (!t || !(t === view.dom || t.classList?.contains('pg-sp') || t.closest?.('.pg-sp'))) return false;
  const L = lastLayout;
  const g = sheetY(view, e.clientY);
  if (!L || !g) return false;
  const idx = Math.max(0, Math.min(L.pages.length - 1, Math.floor(g.y / STRIDE)));
  const yin = g.y - idx * STRIDE;
  const pg = L.pages[idx];
  const atoms = L.measured.atoms;
  let target: { y: number; end: boolean } | null = null;
  const lastBottom = pg.lastBottom - idx * STRIDE;
  if (yin > lastBottom - 1) target = { y: pg.lastBottom - 4, end: true };
  else if (yin < BODY_TOP) target = { y: contentTop(idx) + 4, end: false };
  if (!target) return false;
  const r = view.dom.getBoundingClientRect();
  const host = g.host.getBoundingClientRect();
  const cy = host.top + target.y * g.k;
  const cx = target.end ? r.right - 2 : Math.max(r.left + 2, Math.min(r.right - 2, e.clientX));
  const found = view.posAtCoords({ left: cx, top: cy });
  if (!found) return false;
  void atoms;
  e.preventDefault();
  const $p = view.state.doc.resolve(found.pos);
  const sel = e.shiftKey ? TextSelection.create(view.state.doc, view.state.selection.anchor, $p.pos) : TextSelection.near($p);
  view.dispatch(view.state.tr.setSelection(sel).scrollIntoView());
  view.focus();
  return true;
}

let goalX: number | null = null;
/** Стрелки вверх/вниз на границе листов: переход на последнюю строку предыдущего / первую строку следующего листа. */
function verticalNav(view: EditorView, e: KeyboardEvent): boolean {
  if ((e.key !== 'ArrowDown' && e.key !== 'ArrowUp') || e.ctrlKey || e.altKey || e.metaKey) { goalX = null; return false; }
  const L = lastLayout;
  if (!L || !pagesEnabled(view)) return false;
  const sel = view.state.selection;
  const $h = sel.$head;
  for (let d = $h.depth; d > 0; d--) if (/^table/.test($h.node(d).type.name)) return false;   // таблицы - сами
  if (!$h.parent.isTextblock) return false;
  let c;
  try { c = view.coordsAtPos(sel.head); } catch { return false; }
  const g = sheetY(view, (c.top + c.bottom) / 2);
  if (!g) return false;
  const idx = Math.max(0, Math.min(L.pages.length - 1, Math.floor(g.y / STRIDE)));
  const pg = L.pages[idx];
  const lineH = (c.bottom - c.top) / g.k;
  const x = goalX ?? c.left;
  let ty: number | null = null;
  if (e.key === 'ArrowDown') {
    if (idx + 1 < L.pages.length && g.y + lineH * 1.2 > pg.lastBottom) ty = contentTop(idx + 1) + L.pages[idx + 1].overhead + 6;
  } else if (idx > 0 && g.y - lineH * 1.2 < contentTop(idx) + pg.overhead) {
    ty = L.pages[idx - 1].lastBottom - 6;
  }
  if (ty === null) { goalX = null; return false; }
  const host = g.host.getBoundingClientRect();
  const found = view.posAtCoords({ left: x, top: host.top + ty * g.k });
  if (!found) return false;
  goalX = x;
  const $p = view.state.doc.resolve(found.pos);
  const ns = e.shiftKey ? TextSelection.create(view.state.doc, sel.anchor, $p.pos) : TextSelection.near($p);
  view.dispatch(view.state.tr.setSelection(ns).scrollIntoView());
  return true;
}

// ------------------------------------------------------------------------------------------------ расширение
/** Позиция документа для щелчка вне строк текста (поля листа, промежуток между листами, серый фон рядом с листом):
 *  как в Word - строка на той же высоте; левее текста - её начало, правее - её конец; в верхнем поле - первая строка
 *  листа, в нижнем (и в промежутке) - последняя; ниже конца текста на последнем листе - конец документа.
 *  Возвращает позицию и признак «объект» (рядом с рисунком/листингом - выделить объект целиком). */
function marginTarget(view: EditorView, cx: number, cy: number): { pos: number; obj: boolean } | null {
  const L = lastLayout;
  const g = sheetY(view, cy);
  if (!L || !g || !L.pages.length) return null;
  const n = L.pages.length;
  const idx = Math.max(0, Math.min(n - 1, Math.floor(g.y / STRIDE)));
  const pg = L.pages[idx];
  if (idx === n - 1 && g.y > pg.lastBottom) return { pos: TextSelection.atEnd(view.state.doc).head, obj: false };
  const top = contentTop(idx) + (pg.overhead || 0);
  let y = g.y;
  if (y < top + 3) y = top + 4;
  if (y > pg.lastBottom - 3) y = pg.lastBottom - 4;
  const r = view.dom.getBoundingClientRect();
  const x = cx <= r.left + 1 ? r.left + 1 : cx >= r.right - 1 ? r.right - 1 : cx;
  const hostR = g.host.getBoundingClientRect();
  const found = view.posAtCoords({ left: x, top: hostR.top + y * g.k });
  if (!found) return null;
  if (found.inside >= 0) {
    const node = view.state.doc.nodeAt(found.inside);
    if (node && !node.isTextblock && ['figure', 'listing', 'formula', 'pageBreak'].includes(node.type.name)) return { pos: found.inside, obj: true };
  }
  // правее строки: позиция на мягком переносе (после пробела) браузер рисует в начале СЛЕДУЮЩЕЙ строки - берём
  // позицию перед пробелом, чтобы курсор встал в конце этой строки, как в Word
  if (x >= r.right - 1 && found.pos > 0) {
    try {
      const cy2 = hostR.top + y * g.k;
      const c = view.coordsAtPos(found.pos, 1);
      const $p = view.state.doc.resolve(found.pos);
      if (c.top > cy2 + 2 && $p.parentOffset > 0 && /\s/.test($p.parent.textBetween($p.parentOffset - 1, $p.parentOffset))) return { pos: found.pos - 1, obj: false };
    } catch { /* нет координат */ }
  }
  return { pos: found.pos, obj: false };
}

function selectAt(view: EditorView, t: { pos: number; obj: boolean }, extend: boolean) {
  const doc = view.state.doc;
  let sel;
  try {
    if (extend) sel = TextSelection.between(doc.resolve(view.state.selection.anchor), doc.resolve(t.pos));
    else if (t.obj) sel = NodeSelection.create(doc, t.pos);
    else sel = TextSelection.near(doc.resolve(t.pos));
  } catch { sel = TextSelection.near(doc.resolve(Math.min(t.pos, doc.content.size))); }
  view.dispatch(view.state.tr.setSelection(sel).scrollIntoView());
}

class PagesView {
  raf = 0;
  ro: ResizeObserver | null = null;
  host: HTMLElement | null = null;
  canvas: HTMLElement | null = null;
  /** Щелчок в поле листа, в промежутке между листами или в сером фоне рядом с листом: курсор в строку на той же
   *  высоте (фокус не теряется - раньше щелчок в левое поле снимал фокус, и набранный текст пропадал молча;
   *  Shift+щелчок в правое поле выделял всё до конца документа). Перетаскивание от поля - выделение строк. */
  onHostDown = (e: MouseEvent) => {
    const v = this.view;
    if (e.button !== 0 || e.defaultPrevented || v.isDestroyed || !pagesEnabled(v)) return;
    const t = e.target as HTMLElement;
    if (!t || v.dom.contains(t)) return;
    const onHost = t === this.host || t.classList?.contains('pg-flow') || !!t.closest?.('.pg-sheets');
    const onCanvas = !onHost && (t === this.canvas || t.classList?.contains('canvas-inner'));
    if (!onHost && !onCanvas) return;
    if (onCanvas) {
      const hr = this.host?.getBoundingClientRect();
      if (!hr || e.clientY < hr.top || e.clientY > hr.bottom) return;       // рядом с титулом и др. листами - как было
    }
    const tg = marginTarget(v, e.clientX, e.clientY);
    if (!tg) return;
    e.preventDefault();
    const a0 = e.shiftKey ? v.state.selection.anchor : -1;
    selectAt(v, tg, e.shiftKey);
    v.focus();
    if (tg.obj && !e.shiftKey) return;
    const anchor = a0 >= 0 ? a0 : v.state.selection.anchor;
    const move = (ev: MouseEvent) => {
      if (!(ev.buttons & 1) || v.isDestroyed) { up(); return; }
      const r = v.dom.getBoundingClientRect();
      let pos: number | null = null;
      if (ev.clientX > r.left && ev.clientX < r.right) { const f = v.posAtCoords({ left: ev.clientX, top: ev.clientY }); pos = f ? f.pos : null; }
      if (pos === null) { const m = marginTarget(v, ev.clientX, ev.clientY); pos = m ? m.pos : null; }
      if (pos === null) return;
      try { v.dispatch(v.state.tr.setSelection(TextSelection.between(v.state.doc.resolve(anchor), v.state.doc.resolve(pos)))); } catch { /* вне текста */ }
    };
    const up = () => { window.removeEventListener('mousemove', move, true); window.removeEventListener('mouseup', up, true); };
    window.addEventListener('mousemove', move, true);
    window.addEventListener('mouseup', up, true);
  };
  constructor(public view: EditorView) {
    this.schedule('init');
    try {
      this.ro = new ResizeObserver(() => this.schedule('resize'));
      this.ro.observe(view.dom);
    } catch { /* нет ResizeObserver */ }
    (document as any).fonts?.ready?.then(() => this.schedule('fonts'));
    view.dom.addEventListener('load', this.onLoad, true);
  }
  onLoad = (e: Event) => { if ((e.target as HTMLElement)?.tagName === 'IMG') this.schedule('img'); };
  schedule(why: string) {
    const h = hostOf(this.view);
    if (h !== this.host) {
      this.host?.removeEventListener('mousedown', this.onHostDown);
      this.canvas?.removeEventListener('mousedown', this.onHostDown);
      this.host = h;
      this.canvas = (h?.closest('.canvas') as HTMLElement | null) || null;
      h?.addEventListener('mousedown', this.onHostDown);
      this.canvas?.addEventListener('mousedown', this.onHostDown);
    }
    if (this.raf) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      if (why === 'doc' && this.fastOk()) { updateCurrentPage(this.view); this.followCaret(); return; }
      this.fast = null;
      layoutNow(this.view, why);
      this.followCaret();
    });
  }
  /** Быстрый путь набора: правка внутри одного абзаца (не в таблице, без распорки внутри), и его высота не
   *  изменилась - всё ниже стоит там же, разметка страниц прежняя; полный пересчёт (замер всего документа и
   *  разбиение, ~15-25 мс на 100+ страницах) не нужен. Абзац стал на строку длиннее/короче - полный пересчёт. */
  fast: { pos: number } | null = null;
  fastOk(): boolean {
    const f = this.fast;
    this.fast = null;
    const dbg = ((window as any).__stoFast ||= { ok: 0, no: {} as Record<string, number> });
    const no = (w: string) => { dbg.no[w] = (dbg.no[w] || 0) + 1; return false; };
    if (!f) return no('noedit');
    if (!lastLayout || !pagesEnabled(this.view)) return no('nolayout');
    let el: HTMLElement | null = null;
    try { el = this.view.nodeDOM(f.pos) as HTMLElement | null; } catch { return false; }
    if (!el || el.nodeType !== 1 || el.tagName !== 'P') return no('el');
    if (el.querySelector('.pg-sp')) return no('spacer');
    const h0 = paraHeights.get(el);
    if (h0 === undefined) return no('noh');
    const r = el.getBoundingClientRect();
    const host = hostOf(this.view);
    const k = host ? host.getBoundingClientRect().width / (host.offsetWidth || 1) || 1 : 1;
    if (Math.abs(r.height / k - h0) >= 0.25) return no('height');
    dbg.ok++;
    return true;
  }
  noteEdit(view: EditorView, prev: any) {
    this.fast = null;
    const dbg = ((window as any).__stoFastNote ||= {} as Record<string, number>);
    const no = (w: string) => { dbg[w] = (dbg[w] || 0) + 1; };
    const a = view.state.doc.content.findDiffStart(prev.doc.content);
    if (a === null || a === undefined) { no('nodiff'); return; }
    const e = view.state.doc.content.findDiffEnd(prev.doc.content);
    if (!e) { no('noend'); return; }
    const doc = view.state.doc;
    const $a = doc.resolve(Math.min(a, doc.content.size));
    const $b = doc.resolve(Math.min(Math.max(e.a, a), doc.content.size));
    if (!$a.parent.isTextblock || $a.parent.type.name !== 'paragraph' || $b.parent !== $a.parent) { no('block'); return; }
    for (let d = $a.depth; d > 0; d--) if (/^table|^footnote/.test($a.node(d).type.name)) { no('table'); return; }
    // ссылка на сноску, формула, xref в правке - полный пересчёт (сноски внизу листа, ширины чипов)
    let atom = false;
    prev.doc.nodesBetween(Math.min(a, prev.doc.content.size), Math.min(e.b, prev.doc.content.size), (n: any) => { if (n.isAtom && n.isInline && !n.isText) atom = true; });
    doc.nodesBetween(a, Math.min(e.a, doc.content.size), (n) => { if (n.isAtom && n.isInline && !n.isText) atom = true; });
    if (atom) { no('atom'); return; }
    no('ok');
    this.fast = { pos: $a.before() };
  }
  /** Правка в фокусе (набор, Ctrl+Alt+1 - раздел с новой страницы): если после пересчёта разметки курсор ушёл за
   *  край окна (текст переехал на следующий лист), прокрутить к нему - раньше вид догонял курсор только на следующей клавише. */
  editAt = 0;
  followCaret() {
    const v = this.view;
    if (!this.editAt || performance.now() - this.editAt > 900 || v.isDestroyed || !v.hasFocus()) return;
    this.editAt = 0;          // один раз на правку: дальше пользователь прокручивает сам
    const cv = (this.host?.closest('.canvas') as HTMLElement | null);
    if (!cv) return;
    try {
      const c = v.coordsAtPos(v.state.selection.head);
      const r = cv.getBoundingClientRect();
      if (c.bottom > r.bottom - 8) cv.scrollBy(0, c.bottom - r.bottom + Math.min(120, r.height * 0.25));
      else if (c.top < r.top + 8) cv.scrollBy(0, c.top - r.top - Math.min(120, r.height * 0.25));
    } catch { /* нет координат */ }
  }
  update(view: EditorView, prev: any) {
    const a = pagesKey.getState(view.state);
    const b = pagesKey.getState(prev);
    if (view.state.doc !== prev.doc && view.hasFocus()) this.editAt = performance.now();
    // быстрый набор (несколько нажатий за кадр) - быстрый путь, пока правки в одном и том же абзаце
    if (view.state.doc !== prev.doc) {
      if (!this.raf) this.noteEdit(view, prev);
      else if (this.fast) { const was = this.fast.pos; this.noteEdit(view, prev); if (this.fast && (this.fast as { pos: number }).pos !== was) this.fast = null; }
    }
    else if (a && b && a.dirty !== b.dirty) this.fast = null;
    if (view.state.doc !== prev.doc || (a && b && a.dirty !== b.dirty)) this.schedule('doc');
    else if (view.state.selection !== prev.selection) updateCurrentPage(view);
  }
  destroy() {
    this.host?.removeEventListener('mousedown', this.onHostDown);
    this.canvas?.removeEventListener('mousedown', this.onHostDown);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.ro?.disconnect();
    this.view.dom.removeEventListener('load', this.onLoad, true);
  }
}

export const PagesExtension = Extension.create({
  name: 'stoPages',
  addProseMirrorPlugins() {
    return [new Plugin<PState>({
      key: pagesKey,
      state: {
        init: () => ({ deco: DecorationSet.empty, spacers: [], dirty: 0 }),
        apply(tr, prev) {
          const m = tr.getMeta(pagesKey);
          if (m && m.deco) return m;
          if (m && m.relayout) return { ...prev, dirty: prev.dirty + 1 };
          if (tr.docChanged) {
            const deco = prev.deco.map(tr.mapping, tr.doc);
            // распорка пропала при отображении (виджет на границе заменённого узла) - список распорок пуст, чтобы
            // следующий пересчёт поставил их заново (spacersInDeco)
            const lost = spacerWidgets(deco) < spacerWidgets(prev.deco);
            return { deco, spacers: lost ? [] : prev.spacers.map((s) => ({ ...s, pos: tr.mapping.map(s.pos, -1) })), dirty: prev.dirty };
          }
          // перенумерация, смена подписей и т. п. (не выделение) - тоже пересчитать
          if (!tr.selectionSet && !m) return { ...prev, dirty: prev.dirty + 1 };
          return prev;
        },
      },
      props: {
        decorations: (s) => pagesKey.getState(s)?.deco,
        handleKeyDown: (view, e) => verticalNav(view, e),
        handleDOMEvents: { mousedown: (view, e) => deadZoneClick(view, e as MouseEvent) },
      },
      view: (v) => new PagesView(v),
    })];
  },
});

/** Пересчитать разметку (смена режима, масштаба, листов перед текстом). */
export function relayout(view: EditorView | null | undefined) {
  if (!view || view.isDestroyed) return;
  view.dispatch(view.state.tr.setMeta(pagesKey, { relayout: true }).setMeta('addToHistory', false));
}

export { BODY_H };
